export const walmartMarketplaceAuthHandoffKey = 'walmartMarketplaceAuthHandoff'
export const walmartMarketplaceCallbackContextKey = 'walmartMarketplaceCallbackContext'

type WalmartMarketplaceAuthHandoff = {
  walmartCallbackUri?: string
  oauthUrlParams?: Record<string, string>
  clientType?: string | null
  timestamp: string
}

const walmartOauthDefaults: Record<string, string> = {
  available_platform_name: 'WALMART',
  country_domain: '.Com',
  marketplace: 'United States',
  proxies: '.Com',
  identifier: 'United States',
}

const getValidWalmartCallbackUri = (value: string | null | undefined) => {
  if (!value) {
    return null
  }

  try {
    const parsedUrl = new URL(value)
    if (!['http:', 'https:'].includes(parsedUrl.protocol)) {
      return null
    }
    return parsedUrl.toString()
  } catch (error) {
    return null
  }
}

const getOauthUrlFromResponse = (response: any) =>
  response?.payload?.data?.url ||
  response?.payload?.data?.data?.url ||
  response?.payload?.data?.oauth_url ||
  response?.payload?.data?.authorization_url ||
  response?.payload?.url ||
  response?.data?.url ||
  response?.data?.oauth_url ||
  response?.data?.authorization_url ||
  null

const getWalmartOauthUrlParamsFromSearch = (queryParams: URLSearchParams) => {
  const params: Record<string, string> = {...walmartOauthDefaults}
  ;[
    'user_id',
    'country_domain',
    'marketplace',
    'proxies',
    'identifier',
    'platform_id',
  ].forEach((key) => {
    const value = queryParams.get(key)
    if (value) {
      params[key] = value
    }
  })

  return params
}

export const getPendingWalmartMarketplaceOauthParams = () => {
  const handoffRaw =
    localStorage.getItem(walmartMarketplaceCallbackContextKey) ||
    localStorage.getItem(walmartMarketplaceAuthHandoffKey)
  if (!handoffRaw) {
    return null
  }

  try {
    const handoff = JSON.parse(handoffRaw || '{}') as WalmartMarketplaceAuthHandoff
    return handoff?.oauthUrlParams || null
  } catch (error) {
    return null
  }
}

export const clearWalmartMarketplaceAuthHandoff = () => {
  localStorage.removeItem(walmartMarketplaceAuthHandoffKey)
}

export const clearWalmartMarketplaceCallbackContext = () => {
  localStorage.removeItem(walmartMarketplaceCallbackContextKey)
}

export const getUserIdFromAuthPayload = (payload: any) =>
  payload?.data?.id ||
  payload?.data?.user_id ||
  payload?.data?.userid ||
  payload?.data?.user?.id ||
  payload?.user_id ||
  payload?.userid ||
  payload?.user?.id ||
  payload?.id ||
  null

export const getWalmartPlatformIdFromPayload = (payload: any) => {
  const candidateData = payload?.data?.data || payload?.data || payload?.results || payload
  const platformList = Array.isArray(candidateData)
    ? candidateData
    : Array.isArray(candidateData?.results)
    ? candidateData.results
    : []

  const walmartPlatform = platformList.find((platform: any) => {
    const normalizedName = `${platform?.name || platform?.code || platform?.display_name || ''}`.toUpperCase()
    return normalizedName === 'WALMART' || normalizedName.includes('WALMART')
  })

  return walmartPlatform?.id || walmartPlatform?.platform_info?.id || null
}

export const captureWalmartMarketplaceHandoffFromSearch = (search: string) => {
  const queryParams = new URLSearchParams(search)
  const source = (queryParams.get('SOURCE') || queryParams.get('source') || '').toUpperCase()
  const rawWalmartCallbackUri = queryParams.get('walmartCallbackUri')
  const walmartCallbackUri = getValidWalmartCallbackUri(rawWalmartCallbackUri)

  if (source !== 'WALMART') {
    return 'ignored'
  }

  if (rawWalmartCallbackUri === null) {
    const oauthUrlParams = getWalmartOauthUrlParamsFromSearch(queryParams)
    const handoff: WalmartMarketplaceAuthHandoff = {
      oauthUrlParams,
      clientType: queryParams.get('clientType'),
      timestamp: new Date().toISOString(),
    }

    try {
      localStorage.setItem(walmartMarketplaceAuthHandoffKey, JSON.stringify(handoff))
      return 'captured'
    } catch (error) {
      return 'invalid'
    }
  }

  if (!rawWalmartCallbackUri || !walmartCallbackUri) {
    return 'invalid'
  }

  const handoff: WalmartMarketplaceAuthHandoff = {
    oauthUrlParams: getWalmartOauthUrlParamsFromSearch(queryParams),
    walmartCallbackUri,
    clientType: queryParams.get('clientType'),
    timestamp: new Date().toISOString(),
  }

  try {
    localStorage.setItem(walmartMarketplaceAuthHandoffKey, JSON.stringify(handoff))
    return 'captured'
  } catch (error) {
    return 'invalid'
  }
}

export const redirectToWalmartMarketplaceAuthIfNeeded = async (
  requestOauthUrl?: (params: Record<string, string>) => Promise<any>,
  extraOauthParams: Record<string, string | number | null | undefined> = {}
) => {
  const handoffRaw = localStorage.getItem(walmartMarketplaceAuthHandoffKey)
  if (!handoffRaw) {
    return false
  }

  let handoff: WalmartMarketplaceAuthHandoff | null = null
  try {
    handoff = JSON.parse(handoffRaw || '{}')
  } catch (error) {
    localStorage.removeItem(walmartMarketplaceAuthHandoffKey)
    return false
  }

  if (requestOauthUrl) {
    try {
      const normalizedExtraParams = Object.entries(extraOauthParams).reduce(
        (acc: Record<string, string>, [key, value]) => {
          if (value !== null && value !== undefined && `${value}` !== '') {
            acc[key] = `${value}`
          }
          return acc
        },
        {}
      )
      const oauthUrlParams = {
        ...walmartOauthDefaults,
        ...(handoff?.oauthUrlParams || {}),
        ...normalizedExtraParams,
      }
      const response = await requestOauthUrl(oauthUrlParams)
      const oauthUrl = getValidWalmartCallbackUri(getOauthUrlFromResponse(response))
      if (oauthUrl) {
        localStorage.removeItem(walmartMarketplaceAuthHandoffKey)
        localStorage.setItem(
          walmartMarketplaceCallbackContextKey,
          JSON.stringify({
            oauthUrlParams,
            clientType: handoff?.clientType,
            timestamp: new Date().toISOString(),
          })
        )
        window.location.assign(oauthUrl)
        return true
      }
    } catch (error) {
      return false
    }

    return false
  }

  const walmartCallbackUri = getValidWalmartCallbackUri(handoff?.walmartCallbackUri)
  if (!walmartCallbackUri) {
    localStorage.removeItem(walmartMarketplaceAuthHandoffKey)
    return false
  }

  localStorage.removeItem(walmartMarketplaceAuthHandoffKey)
  window.location.assign(walmartCallbackUri)
  return true
}
